import { inject, Injectable } from '@angular/core';
import {Firestore, serverTimestamp, setDoc, doc} from '@angular/fire/firestore';
import { CookieService } from 'ngx-cookie-service';
import {EventType, OchOnboardingFunnelEvent, UiAnalyticsSession} from '../models/ui-analytics-session.model';
import { select, Store } from '@ngrx/store';
import { selectUser } from '../store/selectors';
import {createFirestoreId} from '../common/firestore-utils';
import {TenantService} from './tenant.service';
import {arrayUnion, Timestamp} from '@firebase/firestore';
import {environment} from '../../environments/environment';

const production = environment.production;

// ------- Tracking Data Model --------

/**
 * Internal type representing the structure of the `och-tracking` cookie.
 */
interface TrackingData {
  deviceId?: string;    // Persistent per device (browser), does not reset
  sessionId?: string;   // Resets after 30 minutes of inactivity
  lastActivity?: number; // Timestamp of last activity (in milliseconds)
  createdAt?: number;   // Timestamp of cookie creation (in milliseconds)
}


@Injectable({
  providedIn: 'root'
})
export class OnboardingAnalyticsService {

  private readonly TRACKING_COOKIE = 'och-tracking';
  private readonly SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

  firestore = inject(Firestore);
  cookieService = inject(CookieService);
  store = inject(Store);
  tenantService = inject(TenantService);
  tracking: TrackingData = {};
  index = 1
  isSecure: boolean = production;

  private userId: string | null = null;

  constructor() {

    this.ensureTrackingCookieWithSessionLogic();

    this.store.pipe(select(selectUser)).subscribe(user => {
      this.userId = user?.id ?? null;
    });

  }

  async reportSuccessfulLoginType(providerId: string) {

    console.log('saving login successful event providerIn: ', providerId);

    if (providerId === 'google.com') {
      await this.reportLoginWithGoogleSuccessful({loginTypeProviderId: providerId});
    } else {
      await this.reportLoginWithPasswordSuccessful({loginTypeProviderId: providerId});
    }
  }

  async reportLoginPageView(extraInfo: any = {}) {
    console.log('###report loginPageView');
    await this.sendOnboardingEvent('login-page-view', extraInfo);
  }

  async reportLoginWithGoogleStarted(extraInfo: any = {}) {
    await this.sendOnboardingEvent('login-with-google-started', extraInfo);
  }

  async reportLoginWithGoogleSuccessful(extraInfo: any = {}) {
    await this.sendOnboardingEvent('login-with-google-successful', extraInfo);
  }

  async reportLoginWithGoogleError(extraInfo: any = {}) {
    await this.sendOnboardingEvent('login-with-google-error', extraInfo);
  }

  async reportLoginWithPasswordStarted(extraInfo: any = {}) {
    await this.sendOnboardingEvent('login-with-password-started', extraInfo);
  }

  async reportLoginWithPasswordSuccessful(extraInfo: any = {}) {
    await this.sendOnboardingEvent('login-with-password-successful', extraInfo);
  }

  async reportLoginWithPasswordError(extraInfo: any = {}) {
    await this.sendOnboardingEvent('login-with-password-error', extraInfo);
  }

  async reportSignupPageView(extraInfo: any = {}) {
    await this.sendOnboardingEvent('signup-page-view', extraInfo);
  }

  async reportSignupWithGoogleStarted(extraInfo: any = {}) {
    await this.sendOnboardingEvent('signup-with-google-started', extraInfo);
  }

  async reportSignupWithGoogleSuccessful(extraInfo: any = {}) {
    await this.sendOnboardingEvent('signup-with-google-successful', extraInfo);
  }

  async reportSignupWithGoogleError(extraInfo: any = {}) {
    await this.sendOnboardingEvent('signup-with-google-error', extraInfo);
  }

  async reportSignupWithPasswordStarted(extraInfo: any = {}) {
    await this.sendOnboardingEvent('signup-with-password-started', extraInfo);
  }

  async reportSignupWithPasswordSuccessful(extraInfo: any = {}) {
    await this.sendOnboardingEvent('signup-with-password-successful', extraInfo);
  }

  async reportSignupWithPasswordError(extraInfo: any = {}) {
    await this.sendOnboardingEvent('signup-with-password-error', extraInfo);
  }

  async reportTenantAccountCreated(extraInfo: any = {}) {
    await this.sendOnboardingEvent('tenant-account-created', extraInfo);
  }

  async reportTenantHearAboutUsAnswered(extraInfo: any = {}) {
    await this.sendOnboardingEvent('tenant-heard-about-us-answered', extraInfo);
  }

  async reportTenantQuestionnaireCompleted(extraInfo: any = {}) {
    await this.sendOnboardingEvent('tenant-questionnaire-completed', extraInfo);
  }


  /**
   * Sends an onboarding funnel event to Firestore.
   * Enriches the event with deviceId, sessionId, and userId if available.
   * This method ensures every event has the correct tracking context.
   */
  private async sendOnboardingEvent(event: EventType, extraInfo: any) {

    const tracking = this.getTrackingCookie();

    if (!tracking) {
      console.log('tracking data is empty');
      return;
    }

    console.log("tracking after event: ", tracking);
    console.log('saving event', event);

    const eventRecord = {
      type: 'onboarding-funnel',
      deviceId: tracking.deviceId,
      sessionId: tracking.sessionId,
      userId: this.userId ?? null,
      tenantId: this?.tenantService?.id ?? null,
      events: arrayUnion({
        event,
        createdAt: Timestamp.now(),
      }),
      extraInfo: {
        ...extraInfo,
        ...this.getDeviceInfo(),
      },
      createdAt: Timestamp.fromMillis(tracking?.createdAt) || serverTimestamp(),
      updatedAt: serverTimestamp(),

    } as OchOnboardingFunnelEvent;


    console.log('saving event', eventRecord);


    await this.saveEventRecord(eventRecord);

  }

  private ensureTrackingCookieWithSessionLogic() {

    console.log('###ensureTrackingCookieWithSessionLogic');
    let tracking = this.getTrackingCookie();

    console.log("tracking data: ", tracking);

    if (!tracking || !tracking?.sessionId) {

      console.log('tracking cookie does not exist or is corrupted', tracking);
      console.log('generating deviceId and sessionId');

      tracking = {
        deviceId: this.generateId(),
        sessionId: this.generateId(),
        createdAt: Date.now()
      };
    }

    const isCookieExpired =  this.checkIfCookieSessionExpired(tracking);

    if (isCookieExpired) {

      console.log('tracking cookie expired', tracking);

      tracking.sessionId = this.generateId();
      tracking.createdAt = Date.now();
    }

    console.log('tracking cookie exists and is active', tracking);

    tracking.lastActivity = Date.now();

    this.setTrackingCookie(tracking);
  }

  private checkIfCookieSessionExpired(tracking: TrackingData): boolean {
    console.log('current tracking session', tracking);

    const now = Date.now();

    if (tracking?.lastActivity && (now - tracking.lastActivity) > this.SESSION_TIMEOUT_MS) {
      console.log('Session expired based on lastActivity timestamp in cookie.');
      return true;
    }

    return false;
  }

  private async saveEventRecord(event: UiAnalyticsSession) {
    const path = `uiAnalyticsSessions/${event.sessionId}`;
    const eventRef = doc(this.firestore, path);
    await setDoc(eventRef, event, {merge: true});
  }

  /**
   * ExtraInfo: Gets essential device information that could help understand user drop-off during onboarding.
   * Focuses on information that might indicate technical issues or user behavior patterns.
   */
  getDeviceInfo() {
    const userAgent = navigator.userAgent;
    const language = navigator.language;

    // Device type detection - helps understand if mobile users have different drop-off rates
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isTablet = /(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent);
    const deviceType = isTablet ? 'tablet' : (isMobile ? 'mobile' : 'desktop');

    // Screen size - helps identify if users on smaller screens have more issues
    const screenSize = {
      width: window.screen.width,
      height: window.screen.height,
    };

    // Geographic information
    const approxGeoLocation = {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      country: navigator.language.split('-')[1] || navigator.language,
      region: navigator.language.split('-')[0],

    };

    return {
      device: {
        type: deviceType,
        screenSize
      },
      userAgent,
      language,
      referrer: document.referrer,
      approxGeoLocation
    };
  }

  /**
   * Reads and parses the `och-tracking` cookie.
   * If the cookie is missing or corrupted, returns an empty object.
   */
  private getTrackingCookie(): TrackingData {
    try {

      const raw = this.cookieService.get(this.TRACKING_COOKIE);
      const parsed = raw ? JSON.parse(raw) : {};

      console.log('Parsed cookie value:', parsed);

      return parsed;

    } catch (err) {

      console.error('Error reading cookie:', err);

      return {};
    }
  }

  /**
   * Writes the given tracking data into the `och-tracking` cookie.
   * This uses a session cookie (expires when browser is closed).
   */
  private setTrackingCookie(data: TrackingData) {

    console.log('Setting tracking cookie with data:', data);

    const json = JSON.stringify(data);

    const hostParts = window.location.hostname.split('.');
    const domain = hostParts.length > 2 ? `.${hostParts.slice(-2).join('.')}` : window.location.hostname;

    this.cookieService.set(
      this.TRACKING_COOKIE,
      json,
      {
        path: '/',
        domain: domain,
        secure: this.isSecure
      }
    );


    console.log('Cookie set successfully', domain);
  }

  private generateId(): string {
    return createFirestoreId(this.firestore);
  }
}


